import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { FaPlus, FaTrash, FaTools, FaImage } from 'react-icons/fa';
import ConfirmationModal from '../components/ConfirmationModal';

function WorkerInstrumentsPage() {
    const { user, refetchUser } = useAuth();
    const [newInstrument, setNewInstrument] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [description, setDescription] = useState('');
    const [photo, setPhoto] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // State for the confirmation modal
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);

    const handleFileChange = (e) => {
        setPhoto(e.target.files[0]);
    };

    const handleAddInstrument = async (e) => {
        e.preventDefault();
        if (!newInstrument.trim()) {
            setError('Instrument name cannot be empty.');
            return;
        }
        setError('');
        setLoading(true);

        const formData = new FormData();
        formData.append('instrumentName', newInstrument);
        formData.append('quantity', quantity);
        formData.append('description', description);
        if (photo) {
            formData.append('photo', photo);
        }

        try {
            await apiClient.post('/profiles/me/instruments', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setNewInstrument('');
            setQuantity(1);
            setDescription('');
            setPhoto(null);
            document.getElementById('instrument-photo').value = '';

            await refetchUser();
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to add instrument.';
            const details = err.response?.data?.error || '';
            setError(`${msg} ${details}`);
        } finally {
            setLoading(false);
        }
    };

    const openConfirmModal = (instrumentId) => {
        setItemToRemove(instrumentId);
        setIsConfirmOpen(true);
    };

    const handleRemoveInstrument = async () => {
        if (!itemToRemove) return;
        setLoading(true);
        try {
            await apiClient.delete(`/profiles/me/instruments/${itemToRemove}`);
            await refetchUser();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to remove instrument.');
        } finally {
            setLoading(false);
            setIsConfirmOpen(false);
            setItemToRemove(null);
        }
    };

    const handleUpdateQuantity = async (instrumentId, newQuantity) => {
        if (newQuantity < 1) return;
        setLoading(true);
        try {
            await apiClient.put(`/profiles/me/instruments/${instrumentId}`, { quantity: newQuantity });
            await refetchUser();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update quantity.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ConfirmationModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleRemoveInstrument}
                title="Remove Instrument"
                message="Are you sure you want to remove this instrument from your profile?"
            />
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center mb-8">
                    <div className="p-3 bg-green-100 rounded-full mr-4 text-green-600">
                        <FaTools size={24} />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-800">Manage My Instruments</h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Add Instrument Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white p-6 rounded-2xl shadow-lg sticky top-8">
                            <h3 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Add New Instrument</h3>
                            <form onSubmit={handleAddInstrument} className="space-y-4">
                                {/* Form content is unchanged */}
                                <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Instrument Name</label>
                                <input
                                    type="text"
                                    value={newInstrument}
                                    onChange={e => setNewInstrument(e.target.value)}
                                    placeholder="e.g., Tractor, Plow"
                                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                                <input
                                    type="number"
                                    value={quantity}
                                    onChange={e => setQuantity(parseInt(e.target.value, 10) || 1)}
                                    placeholder="Qty"
                                    min="1"
                                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                                <textarea
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    placeholder="Brief description..."
                                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all h-24 resize-none"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Photo (Optional)</label>
                                <div className="relative border border-gray-300 rounded-xl overflow-hidden">
                                    <input
                                        type="file"
                                        id="instrument-photo"
                                        onChange={handleFileChange}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        accept="image/*"
                                        disabled={loading}
                                    />
                                    <div className="p-3 flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                                        <FaImage className="text-gray-400 mr-2" />
                                        <span className="text-gray-600 text-sm truncate">
                                            {photo ? photo.name : 'Choose an image...'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                                <button
                                    type="submit"
                                    className="w-full bg-green-600 text-white p-3 rounded-xl hover:bg-green-700 transition-colors flex items-center justify-center font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                    disabled={loading}
                                >
                                    {loading ? 'Adding...' : <><FaPlus className="mr-2" /> Add Instrument</>}
                                </button>
                            </form>
                            {error && <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100">{error}</div>}
                        </div>
                    </div>

                    {/* Instrument List */}
                    <div className="lg:col-span-2">
                        <div className="bg-white p-6 rounded-2xl shadow-lg">
                            <h3 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">My Instruments Collection</h3>

                            {user?.instruments && user.instruments.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {user.instruments.map(inst => (
                                        <div key={inst.id} className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                                            <div className="h-48 bg-gray-100 overflow-hidden relative">
                                                {inst.photo_path ? (
                                                    <img src={loadImage(inst.photo_path)} alt={inst.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50"><FaTools size={48} className="mb-2 opacity-20" /><span className="text-sm">No Image</span></div>
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="text-lg font-bold text-gray-800">{inst.name}</h4>
                                                    <div className="flex items-center bg-gray-100 rounded-lg px-2 py-1">
                                                        <button onClick={() => handleUpdateQuantity(inst.id, (inst.Worker_Instruments?.quantity || 1) - 1)} className="text-gray-500 hover:text-red-500 px-1 font-bold" disabled={loading || (inst.Worker_Instruments?.quantity || 1) <= 1}>-</button>
                                                        <span className="mx-2 text-sm font-bold text-gray-700">{inst.Worker_Instruments?.quantity || 1}</span>
                                                        <button onClick={() => handleUpdateQuantity(inst.id, (inst.Worker_Instruments?.quantity || 1) + 1)} className="text-gray-500 hover:text-green-500 px-1 font-bold" disabled={loading}>+</button>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-gray-500 line-clamp-2 min-h-[2.5rem] mb-4">{inst.description || 'No description provided.'}</p>
                                                <div className="flex justify-end border-t pt-3">
                                                    <button onClick={() => openConfirmModal(inst.id)} className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors flex items-center text-sm font-medium" disabled={loading}><FaTrash className="mr-1.5" /> Remove</button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                    <FaTools size={48} className="mx-auto text-gray-300 mb-4" />
                                    <p className="text-gray-500 font-medium">You haven't added any instruments yet.</p>
                                    <p className="text-gray-400 text-sm mt-1">Add your equipment to showcase your capabilities.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default WorkerInstrumentsPage;
